import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from "recharts";
import {
  Instagram, TrendingUp, Users, Heart, MessageCircle, Share2, Bookmark,
  Play, Eye, Sparkles, Loader2, MapPin, Info, ArrowUpRight, Flame, Lock, LogOut,
  Link2, RefreshCw, CheckCircle2, Copy, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IG_GRAD = "linear-gradient(90deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)";
const nf = (n) => Number(n || 0).toLocaleString("it-IT");
const compact = (n) => {
  n = Number(n || 0);
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".", ",") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(".", ",") + "k";
  return String(n);
};
const dayLabel = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

function KpiCard({ icon: Icon, label, value, delta, tone = "default", testid }) {
  return (
    <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5 backdrop-blur" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Icon size={18} className="text-neutral-400" />
        {delta != null && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${tone === "up" ? "text-emerald-400" : "text-neutral-400"}`}>
            <ArrowUpRight size={13} /> {delta}
          </span>
        )}
      </div>
      <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <p className="font-bold text-2xl md:text-3xl mt-1 text-neutral-50">{value}</p>
    </div>
  );
}

function SectionTitle({ children, sub }) {
  return (
    <div className="mb-5">
      <h2 className="font-bold text-xl text-neutral-50">{children}</h2>
      {sub && <p className="text-sm text-neutral-500 mt-0.5">{sub}</p>}
    </div>
  );
}

const AGE_COLORS = ["#feda75", "#fa7e1e", "#d62976", "#962fbf", "#4f5bd5"];

function LockScreen({ onUnlock }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/ig/unlock`, { password: pwd });
      localStorage.setItem("ig_token", data.token);
      onUnlock(data.token);
    } catch {
      setError("Password errata. Riprova.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 grid place-items-center px-4" data-testid="ig-lock-screen">
      <Toaster theme="dark" position="top-right" />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-neutral-900/70 border border-neutral-800 rounded-3xl p-8 text-center backdrop-blur">
        <div className="inline-grid place-items-center w-16 h-16 rounded-2xl mb-5" style={{ background: IG_GRAD }}>
          <Lock size={26} className="text-white" />
        </div>
        <h1 className="font-bold text-xl">Insight Studio</h1>
        <p className="text-sm text-neutral-500 mt-1 mb-6">Area privata · inserisci la password per accedere</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="password" value={pwd} autoFocus placeholder="Password"
            onChange={(e) => setPwd(e.target.value)} data-testid="ig-password-input"
            className="w-full bg-neutral-950 border border-neutral-700 focus:border-neutral-400 rounded-xl py-3 px-4 text-center text-neutral-100 focus:outline-none transition-colors" />
          {error && <p className="text-sm text-rose-400" data-testid="ig-password-error">{error}</p>}
          <button type="submit" disabled={loading || !pwd} data-testid="ig-unlock-button"
            className="w-full flex items-center justify-center gap-2 rounded-xl text-white font-semibold py-3 disabled:opacity-60 transition-opacity"
            style={{ background: IG_GRAD }}>
            {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={16} />} Accedi
          </button>
        </form>
      </motion.div>
    </div>
  );
}

export default function InstagramAnalytics() {
  const [token, setToken] = useState(() => localStorage.getItem("ig_token") || "");
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [reels, setReels] = useState([]);
  const [sort, setSort] = useState("views");
  const [demo, setDemo] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggSource, setSuggSource] = useState(null);
  const [loadingSugg, setLoadingSugg] = useState(false);
  const [scripts, setScripts] = useState({});
  const [loadingScript, setLoadingScript] = useState(null);
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const cfg = useCallback(() => ({ headers: { "X-IG-Token": token } }), [token]);

  const handle401 = useCallback((e) => {
    if (e?.response?.status === 401) {
      localStorage.removeItem("ig_token");
      setToken("");
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/instagram/status`, cfg());
      setStatus(data);
    } catch (e) { handle401(e); }
  }, [cfg, handle401]);

  const loadReels = useCallback(async (s) => {
    try {
      const { data } = await axios.get(`${API}/ig/reels`, { params: { sort: s }, ...cfg() });
      setReels(data.reels || []);
    } catch (e) { handle401(e); }
  }, [cfg, handle401]);

  const loadAll = useCallback(async () => {
    try {
      const [ov, fol, dem] = await Promise.all([
        axios.get(`${API}/ig/overview`, cfg()),
        axios.get(`${API}/ig/followers`, { params: { days: 30 }, ...cfg() }),
        axios.get(`${API}/ig/demographics`, cfg()),
      ]);
      setOverview(ov.data);
      setSeries((fol.data.series || []).map((d) => ({ ...d, label: dayLabel(d.date) })));
      setDemo(dem.data);
    } catch (e) { handle401(e); if (e?.response?.status !== 401) toast.error("Errore nel caricamento dei dati"); }
  }, [cfg, handle401]);

  useEffect(() => {
    if (!token) return;
    loadStatus();
    loadAll();
    loadReels("views");
    // gestisci ritorno dal collegamento OAuth
    const params = new URLSearchParams(window.location.search);
    const ig = params.get("ig");
    if (ig) {
      if (ig === "connected") toast.success("Instagram collegato! Premi 'Sincronizza' per caricare i dati.");
      else if (ig === "noaccount") toast.error("Nessun account Instagram professionale trovato sulla Pagina Facebook.");
      else if (ig === "error") toast.error("Collegamento non riuscito. Riprova.");
      window.history.replaceState({}, "", "/instagram");
    }
  }, [token, loadStatus, loadAll, loadReels]);

  const connectInstagram = async () => {
    try {
      const { data } = await axios.get(`${API}/instagram/oauth/start`, cfg());
      window.location.href = data.url;
    } catch (e) {
      handle401(e);
      toast.error(e?.response?.data?.detail || "Configurazione Meta mancante");
    }
  };

  const syncInstagram = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/instagram/sync`, {}, cfg());
      toast.message("Sincronizzazione avviata… recupero dati da Instagram (può richiedere ~1 min).");
      // polling dello stato finché il sync in background termina
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/instagram/status`, cfg());
          setStatus(data);
          if (!data.syncing) {
            clearInterval(poll);
            setSyncing(false);
            if (data.sync_error) {
              toast.error(`Sync non riuscita: ${data.sync_error}`);
            } else {
              toast.success("Dati Instagram aggiornati!");
              await loadAll();
              await loadReels(sort);
            }
          }
        } catch (e) { clearInterval(poll); setSyncing(false); handle401(e); }
      }, 5000);
    } catch (e) {
      setSyncing(false);
      handle401(e);
      if (e?.response?.status !== 401) toast.error(e?.response?.data?.detail || "Sincronizzazione fallita");
    }
  };

  const changeSort = (s) => { setSort(s); loadReels(s); };

  const genSuggestions = async () => {
    setLoadingSugg(true);
    try {
      const { data } = await axios.post(`${API}/ig/suggestions`, { goal: "crescita follower ed engagement" }, cfg());
      setSuggestions(data.suggestions || []);
      setSuggSource(data.source);
      toast.success(data.source === "ai" ? "Suggerimenti AI generati!" : "Suggerimenti pronti");
    } catch (e) { handle401(e); if (e?.response?.status !== 401) toast.error("Impossibile generare i suggerimenti"); }
    finally { setLoadingSugg(false); }
  };

  const logout = () => { localStorage.removeItem("ig_token"); setToken(""); };

  const scriptFullText = (s) => [
    `TITOLO: ${s.titolo}`,
    `DURATA: ${s.durata}`,
    "",
    "COPIONE:",
    ...s.sezioni.map((x) => `[${x.tempo}] ${x.testo}`),
    "",
    `CTA: ${s.cta}`,
    "",
    `CAPTION: ${s.caption}`,
    (s.hashtags || []).join(" "),
  ].join("\n");

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copiato negli appunti"); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Copiato"); } catch { toast.error("Impossibile copiare"); }
      document.body.removeChild(ta);
    }
  };

  const genScript = async (i, s) => {
    setLoadingScript(i);
    try {
      const { data } = await axios.post(`${API}/ig/script`,
        { topic: s.topic, hook: s.hook || "", format: s.format || "" }, cfg());
      setScripts((prev) => ({ ...prev, [i]: data }));
    } catch (e) { handle401(e); if (e?.response?.status !== 401) toast.error("Impossibile generare il copione"); }
    finally { setLoadingScript(null); }
  };

  if (!token) return <LockScreen onUnlock={setToken} />;

  const p = overview?.profile;
  const sortTabs = [
    ["views", "Views", Eye], ["likes", "Like", Heart],
    ["comments", "Commenti", MessageCircle], ["saves", "Salvataggi", Bookmark],
  ];
  const maxCity = demo ? Math.max(...demo.cities.map((c) => c.pct)) : 1;

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="instagram-page">
      <Toaster theme="dark" position="top-right" />

      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-neutral-800 bg-neutral-950/90 backdrop-blur px-4 md:px-8 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl" style={{ background: IG_GRAD }}>
              <Instagram size={20} className="text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg leading-none">Insight Studio</h1>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Instagram Analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {overview?.live ? (
              <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-emerald-700/50 text-emerald-400 bg-emerald-500/5" data-testid="live-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
              </span>
            ) : (
              <span className="text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border border-amber-700/50 text-amber-400 bg-amber-500/5" data-testid="demo-badge">
                Dati demo
              </span>
            )}
            <button onClick={logout} data-testid="ig-logout-button" title="Esci"
              className="text-neutral-500 hover:text-white transition-colors">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-12">
        {/* Connection bar */}
        <section data-testid="connection-bar"
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-neutral-900/70 border border-neutral-800 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            {status?.connected ? (
              <>
                <CheckCircle2 size={20} className="text-emerald-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-neutral-100">
                    Instagram collegato{status.username ? ` · @${status.username}` : ""}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {status.last_sync ? `Ultima sincronizzazione: ${new Date(status.last_sync).toLocaleString("it-IT")}` : "Non ancora sincronizzato"}
                  </p>
                </div>
              </>
            ) : (
              <>
                <Instagram size={20} className="text-neutral-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-neutral-100">Account non collegato</p>
                  <p className="text-xs text-neutral-500">
                    {status && !status.configured
                      ? "Configura prima l'app Meta (App ID e App Secret)"
                      : "Stai vedendo dati demo. Collega il tuo account per i dati reali."}
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {status?.connected ? (
              <button onClick={syncInstagram} disabled={syncing} data-testid="ig-sync-button"
                className="flex items-center gap-2 rounded-full text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-60 transition-opacity"
                style={{ background: IG_GRAD }}>
                {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Sincronizza
              </button>
            ) : (
              <button onClick={connectInstagram} data-testid="ig-connect-button"
                disabled={status && !status.configured}
                className="flex items-center gap-2 rounded-full text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-50 transition-opacity"
                style={{ background: IG_GRAD }}>
                <Link2 size={16} /> Collega Instagram
              </button>
            )}
          </div>
        </section>

        {/* Avviso scadenza token */}
        {status?.connected && status?.token_expires_at && (() => {
          const daysLeft = Math.ceil((new Date(status.token_expires_at) - new Date()) / 86400000);
          if (daysLeft > 10) return null;
          const expired = daysLeft <= 0;
          return (
            <section data-testid="token-expiry-warning"
              className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl p-4 border ${expired ? "bg-rose-950/40 border-rose-800/60" : "bg-amber-950/30 border-amber-800/50"}`}>
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className={expired ? "text-rose-400 shrink-0" : "text-amber-400 shrink-0"} />
                <div>
                  <p className="text-sm font-semibold text-neutral-100">
                    {expired ? "Collegamento Instagram scaduto" : `Il collegamento Instagram scade tra ${daysLeft} giorn${daysLeft === 1 ? "o" : "i"}`}
                  </p>
                  <p className="text-xs text-neutral-400">
                    {expired
                      ? "La sincronizzazione è ferma. Ricollega l'account per riprendere ad aggiornare i dati."
                      : "Per evitare che il sync si blocchi, ricollega l'account prima della scadenza (bastano pochi secondi)."}
                  </p>
                </div>
              </div>
              <button onClick={connectInstagram} data-testid="ig-reconnect-button"
                className="flex items-center gap-2 rounded-full text-white font-semibold px-5 py-2.5 text-sm shrink-0 transition-opacity hover:opacity-90"
                style={{ background: IG_GRAD }}>
                <Link2 size={16} /> Ricollega ora
              </button>
            </section>
          );
        })()}

        <section>
          {p && (
            <div className="flex items-center gap-4 mb-6" data-testid="ig-profile">
              <div className="p-[3px] rounded-full" style={{ background: IG_GRAD }}>
                <img src={p.avatar} alt={p.username}
                  className="w-16 h-16 rounded-full object-cover object-top border-2 border-neutral-950" />
              </div>
              <div>
                <p className="font-bold text-lg leading-tight">@{p.username}</p>
                <p className="text-sm text-neutral-400">{p.full_name}</p>
                <p className="text-xs text-neutral-500 mt-0.5">{nf(p.posts)} post · {nf(p.following)} seguiti</p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <KpiCard icon={Users} label="Follower" testid="kpi-followers"
              value={overview ? nf(overview.profile.followers) : "—"}
              delta={overview ? `+${nf(overview.net_growth_30d)} (30gg)` : null} tone="up" />
            <KpiCard icon={TrendingUp} label="Crescita 30 giorni" testid="kpi-growth"
              value={overview ? `+${overview.growth_pct_30d}%` : "—"} />
            <KpiCard icon={Flame} label="Engagement medio" testid="kpi-engagement"
              value={overview ? `${overview.avg_engagement_rate}%` : "—"} />
            <KpiCard icon={Eye} label="Views reel totali" testid="kpi-views"
              value={overview ? compact(overview.total_reel_views) : "—"} />
          </div>
        </section>

        {/* Follower growth */}
        <section data-testid="section-growth">
          <SectionTitle sub="Andamento dei follower negli ultimi 30 giorni">Crescita follower</SectionTitle>
          <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-4 md:p-6">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="folGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#d62976" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#d62976" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                  <XAxis dataKey="label" stroke="#737373" fontSize={11} interval={4} />
                  <YAxis stroke="#737373" fontSize={11} domain={["dataMin - 50", "dataMax + 50"]} tickFormatter={compact} width={44} />
                  <Tooltip
                    contentStyle={{ background: "#171717", border: "1px solid #404040", borderRadius: 12, color: "#fafafa" }}
                    formatter={(v) => [nf(v), "Follower"]} labelStyle={{ color: "#a3a3a3" }} />
                  <Area type="monotone" dataKey="followers" stroke="#d62976" strokeWidth={2.5} fill="url(#folGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {overview?.best_day && (
              <p className="text-xs text-neutral-500 mt-3 flex items-center gap-1.5">
                <TrendingUp size={13} className="text-emerald-400" />
                Giorno migliore: <span className="text-neutral-300">{dayLabel(overview.best_day.date)}</span> con
                <span className="text-emerald-400 font-semibold"> +{overview.best_day.gained}</span> follower
              </p>
            )}
          </div>
        </section>

        {/* Reels ranking */}
        <section data-testid="section-reels">
          <SectionTitle sub="Classifica dei reel per metrica">Reel con le migliori performance</SectionTitle>
          <div className="flex flex-wrap gap-2 mb-4">
            {sortTabs.map(([k, label, Icon]) => (
              <button key={k} onClick={() => changeSort(k)} data-testid={`sort-${k}`}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm transition-colors border ${
                  sort === k ? "bg-white text-neutral-950 border-white font-semibold"
                             : "border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500"}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {reels.map((r, i) => (
                <motion.div key={r.id} layout
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, delay: i * 0.02 }} data-testid="reel-row"
                  className="flex items-center gap-3 md:gap-4 bg-neutral-900/70 border border-neutral-800 rounded-2xl p-3 md:p-4 hover:border-neutral-600 transition-colors">
                  <span className="font-bold text-lg w-7 text-center shrink-0"
                    style={i < 3 ? { background: IG_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" } : { color: "#525252" }}>
                    {i + 1}
                  </span>
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: IG_GRAD }}>
                    <Play size={18} className="text-white fill-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-100 truncate">{r.title}</p>
                    <p className="text-[11px] text-neutral-500 mt-0.5">{r.topic} · {dayLabel(r.date)} · ER {r.engagement_rate}%</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-4 text-xs text-neutral-400 shrink-0">
                    <span className="flex items-center gap-1"><Eye size={13} /> {compact(r.views)}</span>
                    <span className="flex items-center gap-1"><Heart size={13} /> {compact(r.likes)}</span>
                    <span className="flex items-center gap-1"><MessageCircle size={13} /> {compact(r.comments)}</span>
                    <span className="flex items-center gap-1"><Bookmark size={13} /> {compact(r.saves)}</span>
                  </div>
                  <span className="sm:hidden text-xs text-neutral-300 shrink-0 flex items-center gap-1">
                    <Eye size={13} /> {compact(r.views)}
                  </span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </section>

        {/* Demographics */}
        {demo && (
          <section data-testid="section-demographics">
            <SectionTitle sub="Chi ti segue davvero">Demografica del pubblico</SectionTitle>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Age */}
              <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5">
                <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-4">Età</p>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={demo.age} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                      <XAxis dataKey="range" stroke="#737373" fontSize={11} />
                      <YAxis stroke="#737373" fontSize={11} tickFormatter={(v) => `${v}%`} />
                      <Tooltip cursor={{ fill: "#ffffff08" }}
                        contentStyle={{ background: "#171717", border: "1px solid #404040", borderRadius: 12 }}
                        formatter={(v) => [`${v}%`, "Pubblico"]} />
                      <Bar dataKey="pct" radius={[6, 6, 0, 0]}>
                        {demo.age.map((e, i) => <Cell key={i} fill={AGE_COLORS[i % AGE_COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Gender + countries */}
              <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5 flex flex-col">
                <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-4">Genere</p>
                <div className="flex h-4 rounded-full overflow-hidden mb-2">
                  <div style={{ width: `${demo.gender[0].pct}%`, background: "#4f5bd5" }} />
                  <div style={{ width: `${demo.gender[1].pct}%`, background: "#d62976" }} />
                </div>
                <div className="flex justify-between text-sm mb-6">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#4f5bd5" }} /> {demo.gender[0].label} {demo.gender[0].pct}%</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#d62976" }} /> {demo.gender[1].label} {demo.gender[1].pct}%</span>
                </div>
                <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-3">Paesi principali</p>
                <div className="space-y-2">
                  {demo.countries.slice(0, 4).map((c) => (
                    <div key={c.country} className="flex items-center justify-between text-sm">
                      <span className="text-neutral-300">{c.country}</span>
                      <span className="text-neutral-500">{c.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cities */}
              <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5 md:col-span-2">
                <p className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 mb-4 flex items-center gap-1.5">
                  <MapPin size={13} /> Città principali
                </p>
                <div className="space-y-3">
                  {demo.cities.map((c) => (
                    <div key={c.city} data-testid="city-row">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-neutral-200">{c.city}</span>
                        <span className="text-neutral-500">{c.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-neutral-800 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(c.pct / maxCity) * 100}%`, background: IG_GRAD }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* AI suggestions */}
        <section data-testid="section-suggestions">
          <SectionTitle sub="Cosa pubblicare dopo, in base ai tuoi pattern">Suggerimenti AI sui prossimi contenuti</SectionTitle>
          {suggestions.length === 0 ? (
            <div className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-8 text-center">
              <div className="inline-grid place-items-center w-14 h-14 rounded-2xl mb-4" style={{ background: IG_GRAD }}>
                <Sparkles size={24} className="text-white" />
              </div>
              <p className="font-semibold text-neutral-100">Genera idee di contenuto su misura</p>
              <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">
                Analizziamo i tuoi reel con le migliori performance (views, like, commenti) e ti suggeriamo i prossimi topic da trattare — senza consumare crediti.
              </p>
              <button onClick={genSuggestions} disabled={loadingSugg} data-testid="generate-suggestions-button"
                className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full text-white font-semibold disabled:opacity-70 transition-opacity"
                style={{ background: IG_GRAD }}>
                {loadingSugg ? <><Loader2 size={17} className="animate-spin" /> Analizzo i dati…</> : <><Sparkles size={17} /> Genera suggerimenti</>}
              </button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                {suggestions.map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    data-testid="suggestion-card"
                    className="bg-neutral-900/70 border border-neutral-800 rounded-2xl p-5 hover:border-neutral-600 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-neutral-800 text-neutral-300">{s.format}</span>
                        <span className="text-[10px] font-mono uppercase tracking-[0.12em] px-2 py-1 rounded-full"
                          style={s.proven
                            ? { color: "#22c55e", background: "#22c55e18" }
                            : { color: "#a1a1aa", background: "#a1a1aa18" }}>
                          {s.proven ? "Funziona" : "Da testare"}
                        </span>
                      </div>
                      <span className="font-bold text-lg" style={{ background: IG_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>0{i + 1}</span>
                    </div>
                    <h3 className="font-semibold text-neutral-50 leading-snug">{s.topic}</h3>
                    <p className="text-sm text-neutral-400 mt-2">{s.why}</p>
                    {s.hook && (
                      <div className="mt-3 border-l-2 pl-3" style={{ borderColor: "#d62976" }}>
                        <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-0.5">Hook</p>
                        <p className="text-sm text-neutral-200">{s.hook}</p>
                      </div>
                    )}
                    {!scripts[i] ? (
                      <button onClick={() => genScript(i, s)} disabled={loadingScript === i} data-testid={`gen-script-${i}`}
                        className="mt-4 w-full flex items-center justify-center gap-2 rounded-full border border-neutral-700 text-neutral-200 hover:border-neutral-400 py-2.5 text-sm disabled:opacity-60 transition-colors">
                        {loadingScript === i ? <><Loader2 size={15} className="animate-spin" /> Scrivo il copione…</> : <><Sparkles size={15} /> Copione pronto da leggere</>}
                      </button>
                    ) : (
                      <div className="mt-4 bg-neutral-950 border border-neutral-800 rounded-xl p-4" data-testid={`script-${i}`}>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-neutral-100 pr-2">{scripts[i].titolo}</p>
                          <span className="text-[10px] font-mono text-neutral-500 shrink-0">{scripts[i].durata}</span>
                        </div>
                        <div className="space-y-2 mb-3">
                          {scripts[i].sezioni.map((x, k) => (
                            <div key={k} className="flex gap-2">
                              <span className="text-[10px] font-mono uppercase text-neutral-500 pt-0.5 w-14 shrink-0" style={{ color: "#d62976" }}>{x.tempo}</span>
                              <p className="text-sm text-neutral-200 leading-relaxed">{x.testo}</p>
                            </div>
                          ))}
                        </div>
                        <div className="border-l-2 pl-3 mb-3" style={{ borderColor: "#feda75" }}>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-0.5">CTA</p>
                          <p className="text-sm text-neutral-200">{scripts[i].cta}</p>
                        </div>
                        <p className="text-xs text-neutral-400 bg-neutral-900 rounded-lg p-3 mb-2">{scripts[i].caption}</p>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {(scripts[i].hashtags || []).map((h, k) => (
                            <span key={k} className="text-[11px] font-mono text-neutral-500">{h}</span>
                          ))}
                        </div>
                        <button onClick={() => copyText(scriptFullText(scripts[i]))} data-testid={`copy-script-${i}`}
                          className="w-full flex items-center justify-center gap-2 rounded-full text-white font-semibold py-2.5 text-sm transition-opacity hover:opacity-90"
                          style={{ background: IG_GRAD }}>
                          <Copy size={15} /> Copia tutto
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-neutral-600 flex items-center gap-1.5">
                  <Info size={12} /> Basati sui tuoi contenuti con le migliori performance · nessun credito consumato
                </p>
                <button onClick={genSuggestions} disabled={loadingSugg} data-testid="regenerate-suggestions-button"
                  className="text-sm text-neutral-400 hover:text-white flex items-center gap-1.5 transition-colors">
                  {loadingSugg ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Rigenera
                </button>
              </div>
            </>
          )}
        </section>

        <p className="text-center text-[11px] text-neutral-600 pt-4">
          Insight Studio · Dati dimostrativi · Il collegamento all'account Instagram reale verrà attivato in seguito
        </p>
      </main>
    </div>
  );
}
